import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  X,
  FileText,
  Download,
  Save,
  Eye,
  Edit3,
  User,
  Calendar,
  Landmark,
  Loader2,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import api from "../api/axiosInstance";
import { triggerSafeDelete } from "./deleteInterceptor";

const InvoiceManager = () => {
  const [view, setView] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const initialInvoice = {
    id: null,
    client_name: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split("T")[0],
    items: [{ id: Date.now(), description: "", qty: 1, rate: 0, total: 0 }],
    transport_charges: 0,
    material_surcharge: 0,
    gst_rate: 18,
    bank_details:
      "HDFC BANK\nAcc: 50200012345678\nIFSC: HDFC0000123\nBranch: Virar West",
  };

  const [form, setForm] = useState(initialInvoice);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    const res = await api.get("get_invoices.php");
    setInvoices(res.data.invoices || []);
    setLoading(false);
  };

  const fetchNextInvoiceNo = async () => {
    const res = await api.get("get_next_invoice_no.php");
    setForm((prev) => ({ ...prev, invoice_no: res.data.next_no }));
  };

  const handleCreate = () => {
    setForm(initialInvoice);
    fetchNextInvoiceNo();
    setView("create");
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    newItems[index].total =
      parseFloat(newItems[index].qty || 0) *
      parseFloat(newItems[index].rate || 0);
    setForm({ ...form, items: newItems });
  };

  const itemsSubtotal = form.items.reduce((acc, i) => acc + i.total, 0);
  const taxable =
    itemsSubtotal +
    parseFloat(form.transport_charges || 0) +
    parseFloat(form.material_surcharge || 0);
  const gstAmt = 0 * (form.gst_rate / 100);
  const grandTotal = Math.round(taxable + gstAmt);

  const handleSave = async () => {
    setSaveLoading(true);
    const res = await api.post("save_invoice.php", {
      ...form,
      taxable,
      gstAmt,
      grandTotal,
    });
    if (res.data.status === "success") {
      toast.success("Invoice Saved!");
      setView("list");
      loadInvoices();
    }
    setSaveLoading(false);
  };

  const downloadInvoice = async (id, invNo) => {
    const downloadPromise = new Promise(async (resolve, reject) => {
      try {
        const res = await api.get(`generate_invoice_pdf.php?id=${id}`, {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `INV_${invNo.replace(/\//g, "_")}.pdf`);
        link.click();
        resolve();
      } catch (e) {
        reject();
      }
    });

    toast.promise(downloadPromise, {
      loading: "Generating Invoice...",
      success: "Invoice Downloaded!",
      error: "Failed to generate PDF.",
    });
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse text-slate-300">
        LOADING INVOICES...
      </div>
    );

  return (
    <div className="p-4 space-y-8">
      <Toaster position="top-right" />
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-black italic uppercase tracking-tighter">
          Other Invoices
        </h1>
        <button
          onClick={view === "list" ? handleCreate : () => setView("list")}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2"
        >
          {view === "list" ? <Plus size={18} /> : <FileText size={18} />}{" "}
          {view === "list" ? "Create Invoice" : "Back to List"}
        </button>
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b">
              <tr>
                <th className="p-8">Inv No.</th>
                <th className="p-8">Date</th>
                <th className="p-8">Client</th>
                <th className="p-8 text-right">Total</th>
                <th className="p-8 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50 transition-all">
                  <td className="p-8 font-black">#{inv.invoice_no}</td>
                  <td className="p-8 text-xs font-bold text-slate-500">
                    {inv.invoice_date}
                  </td>
                  <td className="p-8 font-bold uppercase">{inv.client_name}</td>
                  <td className="p-8 text-right font-black text-indigo-600">
                    ₹{parseFloat(inv.grand_total).toLocaleString()}
                  </td>
                  <td className="p-1 text-right space-x-1">
                    <button
                      onClick={() => downloadInvoice(inv.id, inv.invoice_no)}
                      className="p-3 bg-indigo-50 text-indigo-600 rounded-xl"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setForm({ ...inv, items: inv.items });
                        setView("create");
                      }}
                      className="p-3 bg-slate-50 text-slate-400 rounded-xl"
                    >
                      <Edit3 size={18} />

                    </button>
                    <button
                      onClick={() => triggerSafeDelete(
                        "invoices",
                        inv.id,
                        `Invoice ${inv.invoice_no} for ₹${inv.grand_total} on ${inv.invoice_date}`,
                        () => { }
                      )}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-[48px] p-12 shadow-2xl border border-slate-100 space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-4">
                Invoice No
              </label>
              <input
                className="w-full p-5 bg-slate-50 rounded-3xl font-black text-indigo-600 outline-none"
                value={form.invoice_no}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-4">
                Client Name
              </label>
              <input
                className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none"
                value={form.client_name}
                onChange={(e) =>
                  setForm({ ...form, client_name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-4">
                Invoice Date
              </label>
              <input
                type="date"
                className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none"
                value={form.invoice_date}
                onChange={(e) =>
                  setForm({ ...form, invoice_date: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xs font-black uppercase text-indigo-600 px-2">
              Line Items
            </h3>
            {form.items.map((item, idx) => (
              <div
                key={item.id}
                className="grid grid-cols-12 gap-4 bg-slate-50/50 p-6 rounded-[32px]"
              >
                <div className="col-span-6">
                  <input
                    className="w-full p-4 bg-white rounded-2xl font-bold text-sm"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      updateItem(idx, "description", e.target.value)
                    }
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full p-4 bg-white rounded-2xl font-black text-center"
                    value={item.qty}
                    onChange={(e) => updateItem(idx, "qty", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full p-4 bg-white rounded-2xl font-black text-center"
                    value={item.rate}
                    onChange={(e) => updateItem(idx, "rate", e.target.value)}
                  />
                </div>
                <div className="col-span-2 text-right font-black self-center">
                  ₹{item.total.toLocaleString()}
                </div>
              </div>
            ))}
            <button
              onClick={() =>
                setForm({
                  ...form,
                  items: [
                    ...form.items,
                    {
                      id: Date.now(),
                      description: "",
                      qty: 1,
                      rate: 0,
                      total: 0,
                    },
                  ],
                })
              }
              className="w-full py-4 border-2 border-dashed border-slate-200 rounded-[30px] text-[10px] font-black uppercase text-slate-400"
            >
              + Add Line
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 border-t pt-10">
            <div className="space-y-4">
              <textarea
                className="w-full p-6 bg-slate-50 rounded-[32px] text-xs font-bold h-32"
                value={form.bank_details}
                onChange={(e) =>
                  setForm({ ...form, bank_details: e.target.value })
                }
              />
              <div className="flex gap-4">
                <input
                  className="flex-1 p-4 bg-slate-50 rounded-2xl text-xs font-bold"
                  placeholder="Transport"
                  type="number"
                  value={form.transport_charges}
                  onChange={(e) =>
                    setForm({ ...form, transport_charges: e.target.value })
                  }
                />
                <input
                  className="flex-1 p-4 bg-slate-50 rounded-2xl text-xs font-bold"
                  placeholder="Surcharge"
                  type="number"
                  value={form.material_surcharge}
                  onChange={(e) =>
                    setForm({ ...form, material_surcharge: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="bg-slate-900 text-white p-10 rounded-[48px] text-right space-y-2">
              <p className="text-slate-500 font-bold uppercase text-[10px]">
                Grand Total
              </p>
              <h3 className="text-7xl font-black italic">
                ₹{grandTotal.toLocaleString()}
              </h3>
            </div>
          </div>

          <button
            disabled={saveLoading}
            onClick={handleSave}
            className="w-full py-7 bg-indigo-600 text-white rounded-[32px] font-black text-xl flex justify-center items-center gap-3"
          >
            {saveLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Save size={24} />
            )}{" "}
            Save Invoice
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceManager;
